export const PRODUCT_NOT_PURCHASABLE = 'PRODUCT_NOT_PURCHASABLE'

export const PRODUCT_NOT_PURCHASABLE_MESSAGE =
  'This article is not purchasable and cannot be shipped. Please resolve the product status or remove it from this shipment.'

/**
 * Inline warning when ship qty exceeds available stock at the selected DC.
 */
export function formatShipmentAvailabilityWarning(available, requested) {
  const avail = Number(available)
  const req = Number(requested)
  if (!Number.isFinite(req) || req <= 0) return null
  if (!Number.isFinite(avail)) return null
  if (req <= avail) return null
  return `No stock at this location for the quantity you are shipping. Available: ${avail}, Required: ${req}.`
}

export function isNonPurchasableAvailability(availRow) {
  if (!availRow) return false
  if (availRow.reason_code === PRODUCT_NOT_PURCHASABLE) return true
  return availRow.is_purchasable === false
}

/**
 * True when this included line is short at a DC that already has a lot.
 * Shipment allocation can oversell that lot, so the user must acknowledge it.
 * Non-purchasable lines are a hard block — never a stock acknowledgement.
 */
export function lineRequiresStockAcknowledgement(availRow, shipQty) {
  if (isNonPurchasableAvailability(availRow)) return false
  const req = Number(shipQty)
  const avail = Number(availRow?.available_quantity)
  if (availRow?.has_lot_at_location !== true) return false
  if (!Number.isFinite(req) || req <= 0) return false
  if (!Number.isFinite(avail)) return false
  return avail < req
}

export function shipmentRequiresStockAcknowledgement(rows) {
  return (rows || []).some((row) =>
    lineRequiresStockAcknowledgement(row?.availRow, row?.shipQty),
  )
}

/** Included (qty > 0) lines that are explicitly not purchasable. */
export function lineIsNonPurchasableBlock(availRow, shipQty) {
  const req = Number(shipQty)
  if (!Number.isFinite(req) || req <= 0) return false
  return isNonPurchasableAvailability(availRow)
}

export function shipmentHasNonPurchasableLine(rows) {
  return (rows || []).some((row) =>
    lineIsNonPurchasableBlock(row?.availRow, row?.shipQty),
  )
}

/** How many products to show before the "View all" link. */
export const NON_PURCHASABLE_BANNER_PREVIEW_LIMIT = 3

/**
 * Human summary for the sticky non-purchasable banner above the ship table.
 * @param {number} count
 * @returns {string}
 */
export function formatNonPurchasableBannerSummary(count) {
  const n = Number(count) || 0
  if (n <= 0) return ''
  if (n === 1) {
    return '1 article in this shipment is not purchasable and cannot be shipped.'
  }
  return `${n} articles in this shipment are not purchasable and cannot be shipped.`
}

/**
 * Display label for a blocked article (article code + name when available).
 * @param {{ articleCode?: string | null, productName?: string | null }} item
 * @returns {string}
 */
export function formatNonPurchasableItemLabel(item) {
  const code = String(item?.articleCode || '').trim()
  const name = String(item?.productName || '').trim()
  if (code && name) return `${code} — ${name}`
  return code || name || 'Unknown article'
}

/**
 * Collect included (qty > 0) non-purchasable order lines and pending catalog adds
 * for the top-of-modal banner. Uses availability rows from shipment-availability.
 *
 * @param {{
 *   lines?: Array<{ id: number|string, product_name?: string, product?: { product_name?: string } }>,
 *   qtyByLine?: Record<string|number, string|number>,
 *   availabilityByLine?: Record<string|number, object>,
 *   pendingAdds?: Array<{
 *     key: string,
 *     admin_product_id: string|number,
 *     quantity?: string|number,
 *     sku?: string,
 *     product_name?: string,
 *   }>,
 *   availabilityByProduct?: Record<string, object>,
 *   getArticleCode?: (line: object) => string | null | undefined,
 * }} params
 * @returns {Array<{
 *   key: string,
 *   kind: 'line' | 'pending',
 *   lineId?: number|string,
 *   pendingKey?: string,
 *   articleCode: string,
 *   productName: string,
 *   label: string,
 * }>}
 */
export function collectNonPurchasableShipmentItems({
  lines = [],
  qtyByLine = {},
  availabilityByLine = {},
  pendingAdds = [],
  availabilityByProduct = {},
  getArticleCode,
} = {}) {
  const items = []

  for (const li of lines) {
    const availRow = availabilityByLine[li.id]
    const shipQty = qtyByLine[li.id]
    if (!lineIsNonPurchasableBlock(availRow, shipQty)) continue
    const articleCode = String(
      availRow?.sku ||
        (typeof getArticleCode === 'function' ? getArticleCode(li) : '') ||
        '',
    ).trim()
    const productName = String(
      availRow?.product_name ||
        li.product_name ||
        li.product?.product_name ||
        '',
    ).trim()
    const entry = {
      key: `line-${li.id}`,
      kind: 'line',
      lineId: li.id,
      articleCode,
      productName,
    }
    items.push({ ...entry, label: formatNonPurchasableItemLabel(entry) })
  }

  for (const add of pendingAdds) {
    const availRow = availabilityByProduct[String(add.admin_product_id)]
    if (!lineIsNonPurchasableBlock(availRow, add.quantity)) continue
    const articleCode = String(availRow?.sku || add.sku || '').trim()
    const productName = String(
      availRow?.product_name || add.product_name || '',
    ).trim()
    const entry = {
      key: `pending-${add.key}`,
      kind: 'pending',
      pendingKey: add.key,
      articleCode,
      productName,
    }
    items.push({ ...entry, label: formatNonPurchasableItemLabel(entry) })
  }

  return items
}

export const INSUFFICIENT_STOCK_ACK_LABEL =
  'I understand that there is insufficient stock at this location for the quantity I am shipping, and proceeding may result in negative stock.'

export const MISSING_LOT_ACK_LABEL =
  'I understand that dispatch will auto-create zero-quantity price lots at this DC for products missing warehouse lots.'

export const COMBINED_ACK_LABEL =
  'I understand that there is insufficient stock and that dispatch will auto-create zero-quantity price lots for products missing warehouse lots.'

/** One checkbox label for stock shortage, missing lots, or both. */
export function shipmentAcknowledgementLabel({
  needsStockAck,
  needsAutoProvisionAck,
}) {
  if (needsStockAck && needsAutoProvisionAck) return COMBINED_ACK_LABEL
  if (needsAutoProvisionAck) return MISSING_LOT_ACK_LABEL
  if (needsStockAck) return INSUFFICIENT_STOCK_ACK_LABEL
  return null
}

/**
 * Identity of everything that should clear the single acknowledgement.
 * Location, quantities, which lines are included, and which conditions apply.
 */
export function shipmentAcknowledgementKey({
  locationId,
  quantities,
  needsStockAck,
  needsAutoProvisionAck,
}) {
  return JSON.stringify({
    locationId: locationId ?? null,
    quantities: quantities || [],
    needsStockAck: !!needsStockAck,
    needsAutoProvisionAck: !!needsAutoProvisionAck,
  })
}

export function acknowledgementAfterChange(checked, previousKey, nextKey) {
  if (previousKey !== nextKey) return false
  return !!checked
}

/**
 * Options passed to createShipment / dispatchOrder.
 * The auto-provision flag is sent only when that condition applies and the
 * unified checkbox is checked. Stock-only acknowledgement stays on the client.
 */
export function shipmentAcknowledgeOptions({
  needsAutoProvisionAck,
  acknowledged,
}) {
  if (needsAutoProvisionAck && acknowledged) {
    return { acknowledgeAutoProvision: true }
  }
  return null
}

/** Maps those options onto the existing shipment request body. */
export function applyAcknowledgeAutoProvision(body, options) {
  if (options?.acknowledgeAutoProvision) {
    body.acknowledge_auto_provision = true
  }
  return body
}

export function getLineRemainingToShip(li) {
  const ordered = li?.quantity || 0
  const shipped = li?.shipped_quantity ?? 0
  return li?.remaining_to_ship ?? Math.max(0, ordered - shipped)
}

/**
 * Notice under a ship-modal line.
 * Non-purchasable takes precedence over stock shortage and missing-lot copy.
 * A backend warning (missing lot / not eligible to auto-provision) is shown as-is.
 * The zero-stock sentence is only used when a lot was found and available qty is short.
 */
export function resolveShipmentLineNotice(availRow, requested) {
  const req = Number(requested)
  if (!Number.isFinite(req) || req <= 0 || !availRow) return null

  if (isNonPurchasableAvailability(availRow)) {
    return availRow.warning
      ? String(availRow.warning)
      : PRODUCT_NOT_PURCHASABLE_MESSAGE
  }

  // A missing lot is not an existing-lot stock shortage.
  if (
    availRow.has_lot_at_location === false ||
    availRow.will_auto_provision === true
  ) {
    return availRow.warning ? String(availRow.warning) : null
  }

  const available = Number(availRow.available_quantity)
  return formatShipmentAvailabilityWarning(
    Number.isFinite(available) ? available : 0,
    req,
  )
}

export function shipmentLineNoticeSeverity(availRow, requested) {
  const req = Number(requested)
  if (!Number.isFinite(req) || req <= 0 || !availRow) return null
  if (isNonPurchasableAvailability(availRow)) return 'error'
  return 'warning'
}
